import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { refreshPinStatus, markLocked } from "@/lib/stockLock";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Building2, Upload, Trash2, ImageIcon, Lock, ShieldCheck, ShieldOff, KeyRound, Landmark, UserCog } from "lucide-react";

const MENU_LABELS = {
  dashboard: "Dashboard",
  products: "Barang",
  invoices: "Faktur Penjualan",
  customers: "Data Pelanggan",
  sales_data: "Data Sales",
  in: "Stok Masuk",
  out: "Stok Keluar",
  capital: "Modal",
  reports: "Laporan",
  settings: "Pengaturan",
  products_edit_category: "Edit Kategori Barang",
  products_view_purchase_price: "Lihat Harga Beli Barang",
};

export default function Settings() {
  const { user, reloadRolePermissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", address2: "", phone: "", email: "", website: "", bank_name: "", bank_account_number: "", bank_account_holder: "" });
  const [logo, setLogo] = useState("");
  const fileRef = useRef(null);

  // ---- PIN state ---------------------------------------------------------
  const [pinSet, setPinSet] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinForm, setPinForm] = useState({ current: "", next: "", confirm: "" });
  const [removePin, setRemovePin] = useState("");

  // ---- Change password state --------------------------------------------
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current || !pwForm.next) {
      toast.error("Password lama & baru wajib diisi");
      return;
    }
    if (pwForm.next.length < 6) {
      toast.error("Password baru minimal 6 karakter");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("Konfirmasi password baru tidak cocok");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      toast.success("Password login diperbarui");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengganti password");
    } finally {
      setPwBusy(false);
    }
  };

  const loadPinStatus = async () => {
    try {
      const r = await api.get("/settings/pin-status");
      setPinSet(!!r.data?.pin_set);
    } catch {
      /* ignore */
    }
  };

  const load = async () => {
    try {
      const r = await api.get("/settings");
      setForm({
        name: r.data.name || "",
        address: r.data.address || "",
        address2: r.data.address2 || "",
        phone: r.data.phone || "",
        email: r.data.email || "",
        website: r.data.website || "",
        bank_name: r.data.bank_name || "",
        bank_account_number: r.data.bank_account_number || "",
        bank_account_holder: r.data.bank_account_holder || "",
      });
      setLogo(r.data.logo || "");
    } catch (err) {
      toast.error("Gagal memuat pengaturan");
    } finally {
      setLoading(false);
    }
  };

  // ---- Role permissions state (owner only) -------------------------------
  const [rolePerms, setRolePerms] = useState(null);
  const [rolePermsBusy, setRolePermsBusy] = useState(false);

  const loadRolePerms = async () => {
    try {
      const r = await api.get("/settings/role-permissions");
      setRolePerms(r.data);
    } catch {
      /* ignore */
    }
  };

  const toggleRolePerm = (role, key) => {
    setRolePerms((rp) => ({
      ...rp,
      [role]: { ...rp[role], [key]: !rp[role][key] },
    }));
  };

  const saveRolePerms = async () => {
    if (!rolePerms) return;
    setRolePermsBusy(true);
    try {
      await api.put("/settings/role-permissions", {
        admin: rolePerms.admin,
        kasir: rolePerms.kasir,
      });
      toast.success("Izin akses role tersimpan. Halaman akan reload.");
      if (reloadRolePermissions) await reloadRolePermissions();
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan izin");
    } finally {
      setRolePermsBusy(false);
    }
  };

  useEffect(() => {
    load();
    loadPinStatus();
    if (user?.role === "owner") loadRolePerms();
  }, [user?.role]);

  const savePin = async (e) => {
    e.preventDefault();
    if (pinForm.next.trim().length < 4) {
      toast.error("PIN minimal 4 karakter");
      return;
    }
    if (pinForm.next !== pinForm.confirm) {
      toast.error("Konfirmasi PIN tidak cocok");
      return;
    }
    setPinBusy(true);
    try {
      const body = { pin: pinForm.next.trim() };
      if (pinSet) body.current_pin = pinForm.current.trim();
      await api.post("/settings/pin", body);
      toast.success(pinSet ? "PIN diperbarui" : "PIN diaktifkan");
      setPinForm({ current: "", next: "", confirm: "" });
      sessionStorage.setItem("stock_pin_unlocked", "1");
      refreshPinStatus();
      await loadPinStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan PIN");
    } finally {
      setPinBusy(false);
    }
  };

  const disablePin = async (e) => {
    e.preventDefault();
    if (!window.confirm("Nonaktifkan PIN keamanan? Semua orang akan bisa mengakses halaman terlindungi.")) return;
    setPinBusy(true);
    try {
      await api.post("/settings/pin/remove", { pin: removePin.trim() });
      toast.success("PIN dinonaktifkan");
      setRemovePin("");
      markLocked();
      refreshPinStatus();
      await loadPinStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menonaktifkan PIN");
    } finally {
      setPinBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Pengaturan tersimpan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar (PNG/JPG)");
      return;
    }
    if (file.size > 800 * 1024) {
      toast.error("Ukuran logo maksimum 800 KB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogo(r.data.logo);
      toast.success("Logo diperbarui");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal upload logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Hapus logo perusahaan?")) return;
    try {
      await api.delete("/settings/logo");
      setLogo("");
      toast.success("Logo dihapus");
    } catch (err) {
      toast.error("Gagal menghapus logo");
    }
  };

  if (loading) {
    return <div className="text-sm text-zinc-500">Memuat pengaturan…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-zinc-900 text-white">
          <Building2 className="h-4 w-4" />
        </span>
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Pengaturan Perusahaan</h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Logo, nama, & alamat perusahaan akan tampil di header faktur cetak.
          </p>
        </div>
      </div>

      {/* Logo section */}
      <Card className="p-5 border-zinc-200 shadow-none rounded-md" data-testid="password-card">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="h-4 w-4 text-zinc-600" />
          <h2 className="text-base font-semibold text-zinc-900">Password Login</h2>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Akun: <span className="font-medium text-zinc-700">{user?.email}</span>. Ganti password login untuk masuk ke aplikasi ini.
        </p>
        <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Password Lama</Label>
            <Input
              data-testid="pw-current"
              type="password"
              value={pwForm.current}
              onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <Label>Password Baru (min 6)</Label>
            <Input
              data-testid="pw-next"
              type="password"
              value={pwForm.next}
              onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>
          <div>
            <Label>Ulangi Password Baru</Label>
            <Input
              data-testid="pw-confirm"
              type="password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button
              type="submit"
              disabled={pwBusy}
              data-testid="pw-submit"
              className="bg-zinc-900 hover:bg-zinc-800"
            >
              {pwBusy ? "Menyimpan…" : "Ganti Password"}
            </Button>
          </div>
        </form>
      </Card>

      {/* Logo section */}
      <Card className="p-5 border-zinc-200 shadow-none rounded-md">
        <div className="flex items-center gap-2 mb-3">
          <ImageIcon className="h-4 w-4 text-zinc-600" />
          <h2 className="text-base font-semibold text-zinc-900">Logo Perusahaan</h2>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          PNG atau JPG, maksimum 800 KB. Rasio persegi (1:1) atau landscape kecil paling ideal.
        </p>
        <div className="flex items-start gap-5 flex-wrap">
          <div
            className="w-32 h-32 rounded-md border border-dashed border-zinc-300 bg-zinc-50 flex items-center justify-center overflow-hidden"
            data-testid="settings-logo-preview"
          >
            {logo ? (
              <img src={logo} alt="Logo perusahaan" className="max-w-full max-h-full object-contain" />
            ) : (
              <ImageIcon className="h-8 w-8 text-zinc-300" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={onPickLogo}
              data-testid="settings-logo-input"
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="btn-upload-logo"
              className="bg-zinc-900 hover:bg-zinc-800 rounded-md"
            >
              <Upload className="h-4 w-4 mr-1.5" />
              {uploading ? "Mengunggah…" : logo ? "Ganti Logo" : "Unggah Logo"}
            </Button>
            {logo && (
              <Button
                type="button"
                variant="outline"
                onClick={removeLogo}
                data-testid="btn-remove-logo"
                className="rounded-md text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Hapus Logo
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Company info section */}
      <Card className="p-5 border-zinc-200 shadow-none rounded-md">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-4 w-4 text-zinc-600" />
          <h2 className="text-base font-semibold text-zinc-900">Identitas Perusahaan</h2>
        </div>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Nama Perusahaan</Label>
            <Input
              data-testid="settings-name"
              placeholder="Contoh: PT Sinar Sejahtera"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <Label>Alamat Office</Label>
            <Input
              data-testid="settings-address"
              placeholder="Jalan, Kota, Kode Pos"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <Label>Alamat Toko</Label>
            <Input
              data-testid="settings-address2"
              placeholder="Cabang, gudang, atau alamat tambahan"
              value={form.address2}
              onChange={(e) => setForm({ ...form, address2: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <Label>Phone</Label>
            <Input
              data-testid="settings-phone"
              placeholder="0812xxxxxxxx"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <Label>Alamat Website</Label>
            <Input
              data-testid="settings-website"
              placeholder="https://perusahaan.com"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
            />
          </div>
          <div className="col-span-2 flex justify-end pt-2">
            <Button
              type="submit"
              disabled={saving}
              data-testid="btn-save-settings"
              className="bg-zinc-900 hover:bg-zinc-800"
            >
              {saving ? "Menyimpan…" : "Simpan Pengaturan"}
            </Button>
          </div>
        </form>
      </Card>

      {/* Bank Info section */}
      <Card className="p-5 border-zinc-200 shadow-none rounded-md" data-testid="bank-info-card">
        <div className="flex items-center gap-2 mb-1">
          <Landmark className="h-4 w-4 text-zinc-600" />
          <h2 className="text-base font-semibold text-zinc-900">Informasi Rekening Bank</h2>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Rekening ini akan tampil di faktur penjualan sebagai instruksi pembayaran transfer.
        </p>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <div className="col-span-2 md:col-span-1">
            <Label>Nama Bank</Label>
            <Input
              data-testid="settings-bank-name"
              placeholder="Contoh: BCA, Mandiri, BRI"
              value={form.bank_name}
              onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Label>Nomor Rekening</Label>
            <Input
              data-testid="settings-bank-number"
              placeholder="Contoh: 1234567890"
              value={form.bank_account_number}
              onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <Label>Atas Nama (a.n.)</Label>
            <Input
              data-testid="settings-bank-holder"
              placeholder="Nama pemilik rekening"
              value={form.bank_account_holder}
              onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })}
            />
          </div>
          <div className="col-span-2 flex justify-end pt-2">
            <Button
              type="submit"
              disabled={saving}
              data-testid="btn-save-bank"
              className="bg-zinc-900 hover:bg-zinc-800"
            >
              {saving ? "Menyimpan…" : "Simpan Rekening"}
            </Button>
          </div>
        </form>
      </Card>

      {/* PIN Security */}
      <Card className="p-5 border-zinc-200 shadow-none rounded-md" data-testid="pin-settings-card">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="h-4 w-4 text-zinc-600" />
          <h2 className="text-base font-semibold text-zinc-900">PIN Keamanan Stok</h2>
          {pinSet ? (
            <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full ml-auto">
              Aktif
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-500 border border-zinc-200 px-2 py-0.5 rounded-full ml-auto">
              Nonaktif
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Setelah PIN diaktifkan, halaman <strong>Barang</strong> hanya bisa dibuka setelah memasukkan PIN sekali per sesi browser.
        </p>

        <form onSubmit={savePin} className="grid grid-cols-2 gap-4">
          {pinSet && (
            <div className="col-span-2">
              <Label>PIN Saat Ini</Label>
              <Input
                data-testid="pin-current"
                type="password"
                inputMode="numeric"
                placeholder="Wajib diisi untuk mengubah PIN"
                value={pinForm.current}
                onChange={(e) => setPinForm({ ...pinForm, current: e.target.value })}
              />
            </div>
          )}
          <div className="col-span-1">
            <Label>{pinSet ? "PIN Baru" : "Buat PIN"}</Label>
            <Input
              data-testid="pin-new"
              type="password"
              inputMode="numeric"
              placeholder="Min 4 karakter"
              value={pinForm.next}
              onChange={(e) => setPinForm({ ...pinForm, next: e.target.value })}
            />
          </div>
          <div className="col-span-1">
            <Label>Konfirmasi PIN</Label>
            <Input
              data-testid="pin-confirm"
              type="password"
              inputMode="numeric"
              placeholder="Ulangi PIN baru"
              value={pinForm.confirm}
              onChange={(e) => setPinForm({ ...pinForm, confirm: e.target.value })}
            />
          </div>
          <div className="col-span-2 flex justify-end pt-1">
            <Button
              type="submit"
              disabled={pinBusy || !pinForm.next.trim()}
              data-testid="btn-save-pin"
              className="bg-zinc-900 hover:bg-zinc-800"
            >
              <ShieldCheck className="h-4 w-4 mr-1.5" />
              {pinBusy ? "Menyimpan…" : pinSet ? "Ganti PIN" : "Aktifkan PIN"}
            </Button>
          </div>
        </form>

        {pinSet && (
          <form onSubmit={disablePin} className="mt-4 pt-4 border-t border-zinc-200 grid grid-cols-2 gap-4">
            <div className="col-span-1">
              <Label className="text-rose-600">Nonaktifkan PIN</Label>
              <Input
                data-testid="pin-disable-current"
                type="password"
                inputMode="numeric"
                placeholder="Masukkan PIN saat ini"
                value={removePin}
                onChange={(e) => setRemovePin(e.target.value)}
              />
            </div>
            <div className="col-span-1 flex items-end justify-end">
              <Button
                type="submit"
                disabled={pinBusy || !removePin.trim()}
                variant="outline"
                data-testid="btn-remove-pin"
                className="rounded-md text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              >
                <ShieldOff className="h-4 w-4 mr-1.5" />
                Nonaktifkan
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Role Permissions — Owner only */}
      {user?.role === "owner" && rolePerms && (
        <Card className="p-5 border-zinc-200 shadow-none rounded-md" data-testid="role-permissions-card">
          <div className="flex items-center gap-2 mb-1">
            <UserCog className="h-4 w-4 text-zinc-600" />
            <h2 className="text-base font-semibold text-zinc-900">Izin Akses per Role</h2>
          </div>
          <p className="text-xs text-zinc-500 mb-4">
            Pilih menu mana yang boleh diakses oleh <strong>Admin</strong> & <strong>Kasir</strong>.
            Owner selalu punya akses penuh. Perubahan berlaku setelah halaman reload.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-widest text-zinc-500">
                  <th className="py-2 pr-3 font-medium">Menu</th>
                  <th className="py-2 px-3 font-medium text-center w-24">Admin</th>
                  <th className="py-2 px-3 font-medium text-center w-24">Kasir</th>
                </tr>
              </thead>
              <tbody>
                {(rolePerms.menu_keys || []).map((key) => (
                  <tr key={key} className="border-b border-zinc-100 last:border-0">
                    <td className="py-2 pr-3 text-zinc-800">
                      {MENU_LABELS[key] || key}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <Switch
                        data-testid={`perm-admin-${key}`}
                        checked={!!rolePerms.admin?.[key]}
                        onCheckedChange={() => toggleRolePerm("admin", key)}
                      />
                    </td>
                    <td className="py-2 px-3 text-center">
                      <Switch
                        data-testid={`perm-kasir-${key}`}
                        checked={!!rolePerms.kasir?.[key]}
                        onCheckedChange={() => toggleRolePerm("kasir", key)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end pt-4">
            <Button
              type="button"
              onClick={saveRolePerms}
              disabled={rolePermsBusy}
              data-testid="btn-save-role-permissions"
              className="bg-zinc-900 hover:bg-zinc-800"
            >
              <ShieldCheck className="h-4 w-4 mr-1.5" />
              {rolePermsBusy ? "Menyimpan…" : "Simpan Izin Akses"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
